import { Modal, ModalBackdrop, ModalCloseButton, ModalContent } from "@/components/ui/modal";

import ArrowLeftIcon from "@/assets/icons/ArrowLeft";
import Close from "@/assets/icons/Close";
import InfoLine from "@/assets/icons/InfoLine";
import AppButton from "@/components/shared/AppButton";
import { Divider } from "@/components/ui/divider";
import { HStack } from "@/components/ui/hstack";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { useT } from "@transifex/react";
import { useRouter } from "expo-router";
import { memo } from "react";

type ModalContentType = "needtologin" | "notice";

const getModalContent = (type: ModalContentType, t: any) => {
  switch (type) {
    case "needtologin":
      return {
        header: t("Warning"),
        message: t("You need to log in to be able to purchase a FlexPass."),
        buttonLabel: t("Log in"),
      };
    case "notice":
      return {
        header: t("Notice"),
        message: t("This pass is available for purchase but is not eligible for immediate use."),
        buttonLabel: t("Proceed"),
      };
  }
};

interface FlexParkModalProps {
  isOpen: boolean;
  onClose: () => void;
  handleConfirm: () => void;
  modalType: ModalContentType;
}

const FlexParkModal: React.FC<FlexParkModalProps> = memo(({ isOpen, onClose, handleConfirm, modalType }) => {
  const router = useRouter();
  const t = useT();
  const { header, message, buttonLabel } = getModalContent(modalType, t);

  const handleButtonClick = () => {
    if (modalType === "needtologin") {
      router.push("/auth/sign-in");
      onClose();
    } else {
      handleConfirm();
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} size="lg">
      <ModalBackdrop />
      <ModalContent className="bg-background-100 max-w-md mx-auto p-0 rounded-[16px]">
        <ModalCloseButton onPress={onClose} className="absolute right-[16px] top-[16px] z-10" data-test-id="close-button" aria-label="Close modal">
          <Close />
        </ModalCloseButton>

        <VStack>
          <VStack className="gap-[16px] items-center px-[20px] py-[32px] m-0">
            <InfoLine height={25} fill="none" stroke="text-typography-900" width={25} />
            <Text className="font-[700] text-[18px] text-center text-typography-900">{header}</Text>
            <Text className="font-[400] leading-[22px] text-[16px] text-center text-typography-900">{message}</Text>
          </VStack>
          <Divider className="bg-outline-300" />
          <HStack className="gap-[10px] p-[20px]">
            <AppButton className="flex-1" onPress={onClose} title={t("Back")} variant="outline" iconPosition="before" icon={ArrowLeftIcon} />
            <AppButton className="flex-1" onPress={handleButtonClick} title={t(buttonLabel)} />
          </HStack>
        </VStack>
      </ModalContent>
    </Modal>
  );
});

export default FlexParkModal;
